import React, { useMemo, useState } from 'react';
import s from '../DietPlanBuilder.module.css';
import { PageHead } from '../BuilderShell';
import { fmtInt } from '../../lib/format';
import type { Template } from '../../types';

export function TemplatePickerDesktop({
  templates,
  loading,
  onPick,
}: {
  templates: Template[] | null;
  loading: boolean;
  onPick: (t: Template | null) => void;
}) {
  const list = templates ?? [];
  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  const selected = useMemo(() => list.find(t => String(t.id) === String(selectedId)), [list, selectedId]);

  return (
    <div className={s.page}>
      <PageHead
        title="Start from a template"
        sub="Its foods fill the meals and set each meal's starting target. You set the daily goals first."
        action={
          <button type="button" className={s.btnOutlineGreen} onClick={() => onPick(null)}>
            Start a blank plan
          </button>
        }
      />
      <div className={s.tableWrap}>
        <div className={s.tableHead}>
          <span />
          <span>Template</span>
          <span>Calorie goal</span>
          <span>Macro split</span>
          <span>Meals</span>
          <span />
        </div>
        {loading && <div className={s.center}>Loading templates…</div>}
        {!loading &&
          list.map(t => {
            const on = String(t.id) === String(selectedId);
            const mealCount = t.meals.length;
            const opts = t.meals.reduce((a, m) => a + m.options.length, 0);
            return (
              <div
                key={String(t.id)}
                className={s.tableRow}
                data-on={on}
                onClick={() => setSelectedId(t.id)}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && setSelectedId(t.id)}
              >
                <span className={s.radio} data-on={on} aria-hidden />
                <span className={s.tplNameCell}>
                  <span className={s.tplName}>{t.name}</span>
                </span>
                <span className={s.tabNum}>{fmtInt(t.goal)}</span>
                <span className={s.tabMuted}>P {t.pct.p} · C {t.pct.c} · F {t.pct.f}</span>
                <span className={s.tabMuted}>{mealCount} · {opts} opt</span>
                <button
                  type="button"
                  className={s.btnUseTpl}
                  data-on={on}
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedId(t.id);
                    onPick(t);
                  }}
                >
                  Use template
                </button>
              </div>
            );
          })}
        {!loading && !list.length && <div className={s.center}>No templates found</div>}
      </div>
      {selected && (
        <div className={s.tableHint}>
          Selected: <b>{selected.name}</b> — click <b>Use template</b> on the row to continue.
        </div>
      )}
    </div>
  );
}
